import { call, put, select, takeLatest, all } from 'redux-saga/effects';
import request from 'utils/request';
import {
  FETCH_PUBLIC_KEY_REQUEST,
  GENERATE_OTP_REQUEST,
  VERIFY_OTP_REQUEST,
  EKYC_STATUS_REQUEST,
  REDIRECT_REQUEST,
} from './constants';
import {
  EKYC_GET_PUBLIC_KEY,
  EKYC_GENERATE_OTP,
  EKYC_VERIFY_OTP,
  EKYC_STATUS,
  EKYC_REDIRECT,
} from '../../utils/urls';
import {
  fetchPublicKeySuccess,
  fetchPublicKeyFailure,
  generateOtpSuccess,
  generateOtpFailure,
  verifyOtpSuccess,
  verifyOtpFailure,
  ekycStatusRequest,
  ekycStatusSuccess,
  ekycStatusFailure,
  redirectKyc,
} from './actions';
import { authFailure } from '../App/actions';
import { encryptPayload } from '../../utils/crypto';

import {
  makeSelectKycId,
  makeSelectErrorCode,
  makeSelectAadhaarNo,
  makeSelectOtpCode,
  makeSelectPublicKey,
} from './selectors';

export function* fetchPublicKey() {
  const requestOptions = {
    method: 'GET',
    credentials: 'include',
    headers: {},
  };

  try {
    const response = yield call(request, EKYC_GET_PUBLIC_KEY, requestOptions);
    if (response.errors) {
      yield put(fetchPublicKeyFailure(response.errors));
    } else {
      yield put(fetchPublicKeySuccess(response));
      yield put(ekycStatusRequest());
    }
  } catch (err) {
    if (
      err.response &&
      (err.response.status === 401 || err.response.status === 403)
    ) {
      yield put(authFailure());
    } else {
      yield put(fetchPublicKeyFailure(err));
    }
  }
}

export function* generateOtp() {
  const kycId = yield select(makeSelectKycId());
  const aadhaarNo = yield select(makeSelectAadhaarNo());
  const publicKey = yield select(makeSelectPublicKey());

  try {
    const encrypted = encryptPayload(publicKey, {
      kyc_id: kycId,
      uid: aadhaarNo,
      consent: 'Y',
    });

    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(encrypted),
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const response = yield call(request, EKYC_GENERATE_OTP, requestOptions);
    if (response.status === 'success') {
      yield put(generateOtpSuccess(response));
    } else {
      if(response.error_code && response.error_code === 'ERROR_INVALID_AADHAAR') {
        yield put(generateOtpFailure(response));
      } else {
        yield put(redirectKyc(response.error_code));
      }
    }
  } catch (err) {
    if (
      err.response &&
      (err.response.status === 401 || err.response.status === 403)
    ) {
      yield put(authFailure());
    } else {
      yield put(generateOtpFailure(err));
    }
  }
}

export function* verifyOtp() {
  const kycId = yield select(makeSelectKycId());
  const aadhaarNo = yield select(makeSelectAadhaarNo());
  const otpCode = yield select(makeSelectOtpCode());
  const publicKey = yield select(makeSelectPublicKey());

  const encrypted = encryptPayload(publicKey, {
    kyc_id: kycId,
    uid: aadhaarNo,
    otp: otpCode,
  });

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(encrypted),
    headers: {
      'Content-Type': 'application/json',
    },
  };

  try {
    const response = yield call(request, EKYC_VERIFY_OTP, requestOptions);
    if (response.errors) {
      yield put(verifyOtpFailure(response.errors));
    } else {
      if (response.status === 'success') {
        yield put(verifyOtpSuccess(response));
        yield put(redirectKyc('NO_ERROR'));
      } else {
        if(response.error_code && response.error_code === 'ERROR_INVALID_OTP') {
          yield put(verifyOtpFailure(response));
        } else {
          yield put(redirectKyc(response.error_code));
        }
      }
    }
  } catch (err) {
    if (
      err.response &&
      (err.response.status === 401 || err.response.status === 403)
    ) {
      yield put(authFailure());
    } else {
      yield put(verifyOtpFailure(err));
    }
  }
}

export function* checkEkycStatus() {
  const kycId = yield select(makeSelectKycId());
  const publicKey = yield select(makeSelectPublicKey());

  const encrypted = encryptPayload(publicKey, { kyc_id: kycId });

  const requestOptions = {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(encrypted),
    headers: {
      'Content-Type': 'application/json',
    },
  };

  try {
    const response = yield call(request, EKYC_STATUS, requestOptions);
    if (response.status === 'KYC_INITIATED') {
      yield put(ekycStatusSuccess(response));
    } else {
      yield put(ekycStatusSuccess(response));
      yield put(redirectKyc('NO_ERROR'));
    }
  } catch (err) {
    if (
      err.response &&
      (err.response.status === 401 || err.response.status === 403)
    ) {
      yield put(authFailure());
    } else {
      yield put(ekycStatusFailure(err));
    }
  }
}

export function* redirectToClient() {
  const kycId = yield select(makeSelectKycId());
  const errorCode = yield select(makeSelectErrorCode());
  let requestURL = `${EKYC_REDIRECT}?kyc_id=${kycId}`;
  if (errorCode) {
    requestURL = `${requestURL}&error_code=${errorCode}`;
  }
  window.open(requestURL, '_self');
}

export function* ekycPageSaga() {
  yield takeLatest(FETCH_PUBLIC_KEY_REQUEST, fetchPublicKey);
  yield takeLatest(GENERATE_OTP_REQUEST, generateOtp);
  yield takeLatest(VERIFY_OTP_REQUEST, verifyOtp);
  yield takeLatest(EKYC_STATUS_REQUEST, checkEkycStatus);
  yield takeLatest(REDIRECT_REQUEST, redirectToClient);
}

export default function* rootSaga() {
  yield all([ekycPageSaga()]);
}
